// Author: FWP Kun Team
//
// One analytics event per backend call, emitted from the axios interceptors.
//
// Why here rather than from each module: Faro's fetch/XHR instrumentation already records every
// request, but it ships them to Tempo as trace spans, and the portal_events collector only reads
// Loki. Nothing about API latency or API failure therefore reaches the dashboard. Emitting from
// the shared axios instance covers every call the portal makes without touching a single module,
// and — unlike a trace — the event carries the module the user was in when it happened.
import { AnalyticsAction, AnalyticsModule, moduleFromPath } from "./taxonomy";
import { track } from "./track";

/**
 * Path segments that are record identifiers rather than route structure.
 *
 * Left as-is, `/tax-invoice/12345` and `/tax-invoice/12346` are two different endpoints to every
 * `group by`, so the response-time panel becomes one bar per record and says nothing. Masking
 * keeps the cardinality at roughly the number of real endpoints.
 */
const ID_SEGMENT = [
  /^\d+$/, // 12345
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, // uuid
  /^[0-9a-f]{24}$/i, // mongo ObjectId
  // Opaque tokens / long slugs. The digit is required: without it this rule masked any long
  // segment, including ordinary camelCase route names, purely by length. `reportCustomerBillings`
  // (22 chars) became `:id` while `reportIcbBacklogs` (17) stayed itself — so one endpoint family
  // split across several keys AND two different reports merged into one row called `:id/search`.
  // Real opaque ids — numeric, uuid, ObjectId, base64-ish tokens — all carry digits and are still
  // caught, by this rule or by the three above it.
  /^(?=.*\d)[A-Za-z0-9_-]{21,}$/,
];

/** Longest path we keep; anything beyond is a query string in disguise. */
const MAX_PATH_LENGTH = 120;

/**
 * Reduce a request URL to a stable endpoint key: no origin, no query string, record ids masked.
 * Exported for tests — this is the whole cardinality contract.
 */
export function maskPath(url: string | undefined): string {
  if (!url) return "unknown";
  let path = url;
  try {
    // Relative URLs need a base to parse; the base itself is discarded.
    path = new URL(url, "http://x").pathname;
  } catch {
    path = url.split("?")[0].split("#")[0];
  }
  const masked = path
    .split("/")
    .map((segment) => (ID_SEGMENT.some((re) => re.test(segment)) ? ":id" : segment))
    .join("/");
  return masked.length > MAX_PATH_LENGTH ? `${masked.slice(0, MAX_PATH_LENGTH)}…` : masked;
}

/** The module the user was looking at when the call was made, not the module that owns the API. */
function currentModule(): AnalyticsModule {
  if (typeof window === "undefined") return AnalyticsModule.Shell;
  return moduleFromPath(window.location.pathname);
}

export interface ApiCallOutcome {
  url?: string;
  method?: string;
  /** HTTP status, or undefined when the request never got a response (network error, timeout). */
  status?: number;
  /** `Date.now()` captured in the request interceptor. */
  startedAt?: number;
  errorMessage?: string;
}

/**
 * Emit one `api.call` event. Never throws: `track()` already swallows, and an analytics failure
 * must not turn into a failed request for the user.
 */
export function trackApiCall(outcome: ApiCallOutcome, result: "success" | "failure"): void {
  const status = outcome.status;
  track(currentModule(), AnalyticsAction.ApiCall, {
    target: maskPath(outcome.url),
    method: (outcome.method || "get").toUpperCase(),
    // A network error has no status; "0" keeps the field present and groupable rather than
    // silently dropped by the attribute normaliser.
    status: typeof status === "number" ? status : 0,
    result,
    duration_ms: outcome.startedAt ? Date.now() - outcome.startedAt : undefined,
    error_message: result === "failure" ? outcome.errorMessage : undefined,
  });
}
